(() => {
  const DEBOUNCE_DELAY = 300;
  const CLOSE_DELAY = 250;
  const MOBILE_BREAKPOINT = 750;

  // Every layer the header stack can paint above the panel. They are stacked, not
  // nested, so each one has to be measured on its own.
  const HEADER_LAYER_SELECTORS = ['.header_panel', '#top-universes', '.ayaq-header-container', '#site-header', '.affiliate-banner'];

  const headerBottom = () => {
    let bottom = 0;

    for (const selector of HEADER_LAYER_SELECTORS) {
      const element = document.querySelector(selector);

      if (!element) continue;

      const rect = element.getBoundingClientRect();

      if (rect.height === 0) continue;

      bottom = Math.max(bottom, rect.bottom);
    }

    return Math.round(bottom);
  };

  const debounce = (callback, wait) => {
    let timer;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => callback(...args), wait);
    };
  };

  class AyaqSearchPanel extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.controller = null;
      this.requestId = 0;
      this.defaultMarkup = null;
      this.isOpen = false;
      this.cardScriptRequested = false;
      this.offsetFrame = null;
      this.filters = { sex: null, storefront: {} };
      this.filtersDrawerOpen = false;
    }

    connectedCallback() {
      this.dialog = this.querySelector('.ayaq-search-panel__dialog');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.status = this.querySelector('[data-search-status]');
      this.trigger = document.getElementById('ayaq-search-trigger');
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.rootUrl = this.dataset.rootUrl || '/';
      this.defaultCollectionHandle = this.dataset.defaultCollection || '';
      this.errorText = this.dataset.errorText || '';
      this.loadingText = this.dataset.loadingText || '';

      this.#bindEvents();
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this.hidden = false;
      this.#syncTopOffset();

      if (window.innerWidth < MOBILE_BREAKPOINT) {
        document.body.classList.add('overflow-hidden');
      }

      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');

      requestAnimationFrame(() => this.classList.add('ayaq-search-panel--open'));

      this.#syncClearButton();
      this.#loadCardScript();
      this.#search(this.input ? this.input.value : '');

      if (this.input) this.input.focus();
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.filtersDrawerOpen = false;
      this.classList.remove('ayaq-search-panel--open');
      document.body.classList.remove('overflow-hidden');
      this.#resetFilters();

      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus) this.trigger.focus();
      }

      setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, CLOSE_DELAY);
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    #bindEvents() {
      if (this.trigger) {
        this.trigger.setAttribute('aria-controls', this.id);
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.addEventListener('click', (event) => {
          // The contact center is now the single entry point for instant
          // product discovery. Keep this legacy full-page panel available when
          // the contact center is disabled, so the header remains functional.
          if (document.querySelector('ayaq-contact')) {
            event.preventDefault();
            document.dispatchEvent(new window.CustomEvent('ayaq:contact-search'));
            return;
          }
          event.preventDefault();
          this.toggle();
        });
      }

      const backdrop = this.querySelector('[data-search-backdrop]');
      if (backdrop) backdrop.addEventListener('click', () => this.close());

      const closeButtons = this.querySelectorAll('[data-search-close]');
      for (const closeButton of closeButtons) {
        closeButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      }

      document.addEventListener('click', (event) => {
        if (!this.isOpen) return;

        const target = event.target;

        // Ignore clicks inside the search dialog
        if (this.dialog && this.dialog.contains(target)) return;

        // Ignore clicks on the trigger (header search toggle handles itself)
        if (this.trigger && (this.trigger === target || this.trigger.contains(target))) return;

        // Ignore clicks inside cart overlays
        if (target.closest && (target.closest('.cart-drawer') || target.closest('.cart-notification'))) return;

        this.close({ restoreFocus: false });
      });

      if (this.input) {
        const search = debounce((term) => this.#search(term), DEBOUNCE_DELAY);

        this.input.addEventListener('input', () => {
          this.#syncClearButton();
          search(this.input.value);
        });
      }

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          if (!this.input) return;

          this.input.value = '';
          this.#syncClearButton();
          this.input.focus();
          this.#search('');
        });
      }

      if (this.results) {
        this.addEventListener('click', (event) => this.#onResultsClick(event));
        this.addEventListener('change', (event) => this.#onFilterChange(event));
      }

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen) return;

        if (this.filtersDrawerOpen) {
          this.#closeFiltersDrawer();
          return;
        }

        this.close();
      });

      // A quick add from the panel opens the cart modal, which is painted below the
      // panel. Focus stays in the modal, so it is not restored to the trigger.
      document.addEventListener('ayaq:cart-modal-open', () => this.close({ restoreFocus: false }));

      window.addEventListener('resize', () => {
        if (this.isOpen) this.#syncTopOffset();
      });

      window.addEventListener(
        'scroll',
        () => {
          if (this.isOpen) this.#syncTopOffset();
        },
        { passive: true }
      );
    }

    #onResultsClick(event) {
      const pill = event.target.closest('[data-search-term]');

      if (pill && this.input) {
        this.input.value = pill.dataset.searchTerm;
        this.#syncClearButton();
        this.input.focus();
        this.#search(this.input.value);
        return;
      }

      if (event.target.closest('[data-search-filters-open]')) {
        if (this.filtersDrawerOpen) {
          this.#closeFiltersDrawer();
        } else {
          this.#openFiltersDrawer();
        }
        return;
      }

      if (event.target.closest('[data-search-filters-close]')) {
        this.#closeFiltersDrawer();
        return;
      }

      if (event.target.closest('[data-search-filters-clear-all]')) {
        this.#clearAllFilters();
        return;
      }

      const activeRemove = event.target.closest('[data-search-active-remove]');

      if (activeRemove) {
        this.#removeActiveFilter(activeRemove);
        return;
      }

      const filterReset = event.target.closest('[data-search-filter-reset]');

      if (filterReset) {
        this.#resetFilterGroup(filterReset.dataset.searchFilterReset);
        return;
      }

      const previous = event.target.closest('[data-scroller-previous]');
      const next = event.target.closest('[data-scroller-next]');

      if (!previous && !next) return;

      const scroller = event.target.closest('[data-scroller]');
      const track = scroller ? scroller.querySelector('[data-scroller-track]') : null;

      if (!track) return;

      const step = track.clientWidth * 0.8;
      track.scrollBy({ left: previous ? -step : step, behavior: 'smooth' });
    }

    #onFilterChange(event) {
      const input = event.target.closest('[data-search-filter-type]');

      if (!input || input.disabled) return;

      const type = input.dataset.searchFilterType;
      const value = input.dataset.searchFilterValue;

      if (type === 'sex') {
        const param = input.dataset.searchFilterParam || 'filter.p.tag';

        for (const other of this.querySelectorAll('[data-search-filter-type="sex"]')) {
          if (other !== input) other.checked = false;
        }

        delete this.filters.storefront[param];
        this.filters.sex = null;

        if (input.checked) {
          this.filters.sex = input.dataset.searchFilterLabel || value;

          if (input.dataset.searchFilterMode === 'client') {
            delete this.filters.storefront[param];
          } else {
            this.filters.storefront[param] = [value];
          }
        }

        this.cache.clear();
        this.defaultMarkup = null;
        this.#search(this.input ? this.input.value : '');
        return;
      }

      if (type === 'facet') {
        const param = input.dataset.searchFilterParam;
        const values = this.filters.storefront[param] || [];

        if (input.checked) {
          if (!values.includes(value)) values.push(value);
          this.filters.storefront[param] = values;
        } else {
          const nextValues = values.filter((entry) => entry !== value);

          if (nextValues.length) {
            this.filters.storefront[param] = nextValues;
          } else {
            delete this.filters.storefront[param];
          }
        }

        this.cache.clear();
        this.defaultMarkup = null;
        this.#search(this.input ? this.input.value : '');
      }
    }

    #resetFilterGroup(group) {
      if (group === 'sex') {
        const sexInput = this.querySelector('[data-search-filter-type="sex"]');
        const param = sexInput ? sexInput.dataset.searchFilterParam : 'filter.p.tag';

        if (param) delete this.filters.storefront[param];
        this.filters.sex = null;
        this.cache.clear();
        this.defaultMarkup = null;
        this.#search(this.input ? this.input.value : '');
        return;
      }

      delete this.filters.storefront[group];
      this.cache.clear();
      this.defaultMarkup = null;
      this.#search(this.input ? this.input.value : '');
    }

    #resetFilters() {
      this.filters = { sex: null, storefront: {} };
      this.cache.clear();
      this.defaultMarkup = null;
    }

    #clearAllFilters() {
      this.#resetFilters();
      this.#closeFiltersDrawer();
      this.#search(this.input ? this.input.value : '');
    }

    #removeActiveFilter(button) {
      const type = button.dataset.searchFilterType;
      const param = button.dataset.searchFilterParam;
      const value = button.dataset.searchFilterValue;

      if (type === 'sex') {
        if (param) delete this.filters.storefront[param];
        this.filters.sex = null;
      } else if (param && value) {
        const values = this.filters.storefront[param] || [];
        const nextValues = values.filter((entry) => entry !== value);

        if (nextValues.length) {
          this.filters.storefront[param] = nextValues;
        } else {
          delete this.filters.storefront[param];
        }
      }

      this.cache.clear();
      this.defaultMarkup = null;
      this.#search(this.input ? this.input.value : '');
    }

    #openFiltersDrawer() {
      const drawer = this.results ? this.results.querySelector('[data-search-filters-drawer]') : null;
      const openButton = this.results ? this.results.querySelector('[data-search-filters-open]') : null;

      if (!drawer) return;

      this.filtersDrawerOpen = true;
      drawer.hidden = false;
      drawer.classList.add('ayaq-search-panel__filters-vertical--open');
      if (openButton) openButton.setAttribute('aria-expanded', 'true');
    }

    #closeFiltersDrawer() {
      const drawer = this.results ? this.results.querySelector('[data-search-filters-drawer]') : null;
      const openButton = this.results ? this.results.querySelector('[data-search-filters-open]') : null;

      this.filtersDrawerOpen = false;

      if (drawer) {
        drawer.classList.remove('ayaq-search-panel__filters-vertical--open');
        drawer.hidden = true;
      }

      if (openButton) openButton.setAttribute('aria-expanded', 'false');
    }

    #escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    #renderActiveFacets() {
      const filtersRoot = this.results ? this.results.querySelector('[data-search-filters]') : null;
      const container = filtersRoot ? filtersRoot.querySelector('[data-search-active-facets]') : null;
      const countBadge = filtersRoot ? filtersRoot.querySelector('[data-search-filters-count]') : null;

      if (!filtersRoot || !container) return;

      const clearAllLabel = filtersRoot.dataset.clearAllLabel || 'Clear all';
      const removeLabel = filtersRoot.dataset.removeFilterLabel || 'Clear';
      const pills = [];
      const seen = new Set();

      for (const input of this.querySelectorAll('[data-search-filter-type]:checked')) {
        const type = input.dataset.searchFilterType;
        const param = input.dataset.searchFilterParam || '';
        const value = input.dataset.searchFilterValue || '';
        const label = input.dataset.searchFilterLabel || '';
        const key = type === 'sex' ? `sex:${label}` : `${param}:${value}`;

        if (seen.has(key)) continue;
        seen.add(key);

        const groupLabel = input.dataset.searchFilterGroupLabel || '';
        const displayLabel = input.dataset.searchFilterDisplayLabel || value;
        const pillText = groupLabel ? `${groupLabel}: ${displayLabel}` : displayLabel;

        pills.push(`
          <button
            type="button"
            class="active-facets__button active-facets__button--light ayaq-search-panel__active-facet"
            data-search-active-remove
            data-search-filter-type="${this.#escapeHtml(type)}"
            data-search-filter-param="${this.#escapeHtml(param)}"
            data-search-filter-value="${this.#escapeHtml(value)}"
            data-search-filter-label="${this.#escapeHtml(label)}"
          >
            <span class="active-facets__button-inner button button--tertiary">
              ${this.#escapeHtml(pillText)}
              <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="10" height="10">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.2" fill="none"/>
              </svg>
              <span class="visually-hidden">${this.#escapeHtml(removeLabel)}</span>
            </span>
          </button>
        `);
      }

      const activeCount = pills.length;

      if (countBadge) {
        countBadge.hidden = activeCount === 0;
        countBadge.textContent = String(activeCount);
      }

      if (!pills.length) {
        container.innerHTML = '';
        container.hidden = true;
        return;
      }

      container.hidden = false;
      container.innerHTML = `
        ${pills.join('')}
        <span class="active-facets__button-wrapper">
          <button type="button" class="active-facets__button-remove underlined-link" data-search-filters-clear-all>
            ${this.#escapeHtml(clearAllLabel)}
          </button>
        </span>
      `;
    }

    #buildFilterQuery() {
      const params = [];

      for (const [param, values] of Object.entries(this.filters.storefront)) {
        for (const value of values) {
          params.push(`${encodeURIComponent(param)}=${encodeURIComponent(value)}`);
        }
      }

      return params.length ? `&${params.join('&')}` : '';
    }

    #cacheKey(term) {
      const storefrontKey = Object.keys(this.filters.storefront)
        .sort()
        .map((param) => {
          const values = [...this.filters.storefront[param]].sort().join(',');
          return `${param}:${values}`;
        })
        .join('|');

      return `${term.trim()}::${this.filters.sex || ''}::${storefrontKey}`;
    }

    #syncTopOffset() {
      if (this.offsetFrame) return;

      this.offsetFrame = requestAnimationFrame(() => {
        this.offsetFrame = null;
        this.style.setProperty('--ayaq-search-panel-top', `${headerBottom()}px`);
      });
    }

    #syncClearButton() {
      if (!this.clearButton || !this.input) return;

      this.clearButton.hidden = this.input.value === '';
    }

    #setBusy(isBusy) {
      if (!this.results) return;

      this.results.setAttribute('aria-busy', isBusy ? 'true' : 'false');

      if (isBusy) this.#announce(this.loadingText);
    }

    #announce(text) {
      if (this.status) this.status.textContent = text;
    }

    #render(markup) {
      if (!this.results) return;

      this.results.innerHTML = markup;
      this.#refreshCards();
      this.#setUpScrollers();
      this.#syncFilterUi();
      this.#renderActiveFacets();
      this.#applyProductFilters();
      this.#initColorSwatches();

      if (this.filtersDrawerOpen) this.#openFiltersDrawer();

      const summary = this.results.querySelector('.ayaq-search-panel__heading, .ayaq-search-panel__message');
      this.#announce(summary ? summary.textContent.trim() : '');
    }

    #renderSpinner() {
      if (!this.results) return;

      this.results.innerHTML = '<span class="ayaq-search-panel__spinner" role="presentation"></span>';
    }

    #renderError() {
      this.#render(`<p class="ayaq-search-panel__message">${this.errorText}</p>`);
    }

    async #search(term) {
      const trimmed = term.trim();

      if (trimmed === '') {
        await this.#renderDefault();
        return;
      }

      this.requestId += 1;
      const requestId = this.requestId;
      const cacheKey = this.#cacheKey(trimmed);

      if (this.cache.has(cacheKey)) {
        this.#setBusy(false);
        this.#render(this.cache.get(cacheKey));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.#setBusy(true);

      const query = encodeURIComponent(trimmed);
      const filterQuery = this.#buildFilterQuery();
      const base = `${this.searchUrl}?q=${query}&options%5Bprefix%5D=last${filterQuery}`;

      try {
        const [products, faq] = await Promise.all([
          this.#fetchSection(`${base}&type=product&section_id=ayaq-search-results`, 'ayaq-search-results'),
          this.#fetchSection(`${base}&type=article&section_id=ayaq-search-faq`, 'ayaq-search-faq'),
        ]);

        const markup = products + faq;
        this.cache.set(cacheKey, markup);

        if (requestId === this.requestId) this.#render(markup);
      } catch (error) {
        if (requestId === this.requestId) this.#renderError();
      } finally {
        if (requestId === this.requestId) this.#setBusy(false);
      }
    }

    async #renderDefault() {
      this.requestId += 1;
      const requestId = this.requestId;
      const cacheKey = this.#cacheKey('');

      if (this.defaultMarkup !== null && this.cache.has(cacheKey)) {
        this.#setBusy(false);
        this.#render(this.cache.get(cacheKey));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.#renderSpinner();
      this.#setBusy(true);

      try {
        const filterQuery = this.#buildFilterQuery();
        const defaultUrl = this.defaultCollectionHandle
          ? `/collections/${encodeURIComponent(this.defaultCollectionHandle)}?section_id=ayaq-search-default${filterQuery}`
          : `${this.rootUrl}?section_id=ayaq-search-default${filterQuery}`;

        this.defaultMarkup = await this.#fetchSection(defaultUrl, 'ayaq-search-default');
        this.cache.set(cacheKey, this.defaultMarkup);

        if (requestId === this.requestId) this.#render(this.defaultMarkup);
      } catch (error) {
        if (requestId === this.requestId) this.#renderError();
      } finally {
        if (requestId === this.requestId) this.#setBusy(false);
      }
    }

    async #fetchSection(url, sectionId) {
      const response = await fetch(url, { signal: this.controller.signal });

      if (!response.ok) throw new Error(`Search section ${sectionId} responded with ${response.status}`);

      const text = await response.text();
      const wrapper = new DOMParser().parseFromString(text, 'text/html').querySelector(`#shopify-section-${sectionId}`);

      return wrapper ? wrapper.innerHTML : '';
    }

    #syncFilterUi() {
      for (const input of this.querySelectorAll('[data-search-filter-type="sex"]')) {
        const label = input.dataset.searchFilterLabel || input.dataset.searchFilterValue;
        input.checked = this.filters.sex === label;
      }

      for (const sexSelected of this.querySelectorAll('[data-search-filter-selected="sex"]')) {
        sexSelected.textContent = this.filters.sex
          ? this.dataset.filterSelectedOne || '1 selected'
          : this.dataset.filterSelectedNone || '0 selected';
      }

      for (const input of this.querySelectorAll('[data-search-filter-type="facet"]')) {
        const param = input.dataset.searchFilterParam;
        const values = this.filters.storefront[param] || [];
        input.checked = values.includes(input.dataset.searchFilterValue);
      }
    }

    #applyProductFilters() {
      if (!this.results) return;

      const sex = this.filters.sex;
      const limit = Number.parseInt(this.dataset.productsLimit, 10) || 8;
      let matched = 0;
      let displayed = 0;

      for (const item of this.results.querySelectorAll('.ayaq-search-panel__grid [data-product-gender]')) {
        const matchesSex = !sex || item.dataset.productGender === sex;

        if (!matchesSex) {
          item.hidden = true;
          continue;
        }

        matched += 1;

        if (matched <= limit) {
          item.hidden = false;
          displayed += 1;
        } else {
          item.hidden = true;
        }
      }

      const countNode = this.results.querySelector('[data-search-products-count], .ayaq-search-panel__count');

      if (countNode) {
        countNode.textContent = `(${displayed})`;
      }
    }

    #initColorSwatches() {
      const databaseNode = document.getElementById('ayaq-search-color-database');

      if (!databaseNode || !this.results) return;

      let colorDatabase = {};

      try {
        colorDatabase = JSON.parse(databaseNode.textContent);
      } catch (error) {
        return;
      }

      for (const swatch of this.results.querySelectorAll('.facet-color[data-color-handle]')) {
        const color = colorDatabase[swatch.dataset.colorHandle];

        if (color) swatch.style.backgroundColor = color;
      }
    }

    // The product cards rely on listeners bound per card by global.js, so they
    // have to be attached again for the freshly injected markup.
    #refreshCards() {
      if (typeof window.bindCardProductEvents === 'function') window.bindCardProductEvents();
      if (typeof window.initProductActivitiesToggles === 'function') window.initProductActivitiesToggles();
    }

    #loadCardScript() {
      const source = this.dataset.cardScript;

      if (!source || this.cardScriptRequested) return;

      this.cardScriptRequested = true;

      if (document.querySelector(`script[src="${source}"]`)) return;

      const script = document.createElement('script');
      script.src = source;
      document.head.appendChild(script);
    }

    #setUpScrollers() {
      for (const scroller of this.querySelectorAll('[data-scroller]')) {
        const track = scroller.querySelector('[data-scroller-track]');
        const nav = scroller.querySelector('[data-scroller-nav]');

        if (!track || !nav) continue;

        if (track.scrollWidth - track.clientWidth < 2) {
          nav.hidden = true;
          continue;
        }

        nav.hidden = false;
        track.addEventListener('scroll', () => this.#updateScroller(scroller), { passive: true });
        this.#updateScroller(scroller);
      }
    }

    #updateScroller(scroller) {
      const track = scroller.querySelector('[data-scroller-track]');
      const bar = scroller.querySelector('[data-scroller-bar]');
      const progress = scroller.querySelector('[data-scroller-progress]');
      const previous = scroller.querySelector('[data-scroller-previous]');
      const next = scroller.querySelector('[data-scroller-next]');
      const maxScroll = track.scrollWidth - track.clientWidth;

      if (previous) previous.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 1;

      if (!bar || !progress) return;

      const visibleRatio = track.clientWidth / track.scrollWidth;
      const barWidth = Math.max(progress.clientWidth * visibleRatio, 24);
      const travelled = maxScroll > 0 ? track.scrollLeft / maxScroll : 0;

      bar.style.width = `${barWidth}px`;
      bar.style.transform = `translateX(${travelled * (progress.clientWidth - barWidth)}px)`;
    }
  }

  customElements.define('ayaq-search-panel', AyaqSearchPanel);
})();
